import React, { useState, useEffect, useCallback } from 'react';
import '../styles/dashboard.css';
import '../styles/logs.css';
import '../styles/leads.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import {
  APP_MODULES,
  emptyModulePermissions,
  modulesObjectFromList,
  serializeAllowedModules
} from '../constants/appModules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield, faPlus, faEdit, faTrash, faTimes, faSearch,
  faUser, faEnvelope, faLock, faToggleOn, faToggleOff, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

const emptyForm = () => ({
  name: '',
  email: '',
  password: '',
  status: 'Active',
  modules: emptyModulePermissions()
});

export default function StaffUsers() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const authHeaders = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/staff-users`, {
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
      else setError(data.message || 'Failed to fetch users');
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedUser(null);
    setFormData(emptyForm());
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setModalMode('edit');
    setSelectedUser(u);
    setFormData({
      name: u.name || '',
      email: u.email || '',
      password: '',
      status: u.status || 'Active',
      modules: modulesObjectFromList(u.allowed_modules)
    });
    setFormError('');
    setShowModal(true);
  };

  const toggleModule = (key) => {
    if (selectedUser?.role === 'admin') return;
    if (key === 'dashboard') return; // always on
    setFormData(prev => ({
      ...prev,
      modules: { ...prev.modules, [key]: !prev.modules[key] }
    }));
  };

  const enabledCount = Object.values(formData.modules || {}).filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        status: formData.status,
        allowedModules: JSON.parse(serializeAllowedModules(formData.modules))
      };
      if (formData.password) payload.password = formData.password;

      if (modalMode === 'create') {
        if (!payload.password) {
          setFormError('Password is required for new staff users');
          setFormLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/staff-users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!data.success) {
          setFormError(data.message || 'Failed to create user');
          return;
        }
      } else {
        const res = await fetch(`${API_URL}/staff-users/${selectedUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!data.success) {
          setFormError(data.message || 'Failed to update user');
          return;
        }
      }

      setShowModal(false);
      fetchUsers();
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/staff-users/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to delete user');
      } else {
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchUsers();
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const filtered = users.filter(u => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  });

  const isEditingAdmin = modalMode === 'edit' && selectedUser?.role === 'admin';

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="leads-container">
          <div className="leads-header">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faUserShield} className="page-icon" />
                Staff Users
              </h1>
              <p style={{ margin: '6px 0 0', color: '#666', fontSize: '13px' }}>
                Manage admin and staff accounts, and control which modules staff can access.
              </p>
            </div>
            <button className="btn-primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
              Add Staff User
            </button>
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '220px' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input
                className="input-field"
                style={{ paddingLeft: '36px', margin: 0 }}
                placeholder="Search by name, email, or role..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="logs-table-container">
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading users...</div>
            ) : (
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Modules</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                        No users found
                      </td>
                    </tr>
                  ) : filtered.map(u => {
                    const mods = Array.isArray(u.allowed_modules) ? u.allowed_modules : [];
                    return (
                      <tr key={u.id}>
                        <td>
                          <strong>{u.name}</strong>
                          {currentUser?.id === u.id && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#F47B20' }}>(you)</span>
                          )}
                        </td>
                        <td>{u.email}</td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            background: u.role === 'admin' ? '#FFF3E0' : '#E3F2FD',
                            color: u.role === 'admin' ? '#E65100' : '#1565C0',
                            textTransform: 'capitalize'
                          }}>
                            {u.role}
                          </span>
                        </td>
                        <td style={{ fontSize: '13px', color: '#555', maxWidth: '320px' }}>
                          {u.role === 'admin'
                            ? <span style={{ color: '#2E7D32', fontWeight: 600 }}>All modules</span>
                            : (mods.length === 0
                              ? <span style={{ color: '#999' }}>None</span>
                              : mods.map(k => APP_MODULES.find(m => m.key === k)?.label || k).join(', '))}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: u.status === 'Inactive' ? '#f3f4f6' : '#dcfce7',
                            color: u.status === 'Inactive' ? '#374151' : '#166534'
                          }}>
                            {u.status || 'Active'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-edit"
                              title="Edit"
                              onClick={() => openEditModal(u)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            {u.role === 'staff' && (
                              <button
                                className="btn-icon btn-delete"
                                title="Delete"
                                onClick={() => { setDeleteTarget(u); setShowDeleteModal(true); }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '820px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={modalMode === 'create' ? faPlus : faEdit} style={{ marginRight: '10px', color: '#F47B20' }} />
                {modalMode === 'create' ? 'Add Staff User' : `Edit ${selectedUser?.role === 'admin' ? 'Admin' : 'Staff'} User`}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {formError && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px' }}>
                    {formError}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label><FontAwesomeIcon icon={faUser} style={{ marginRight: '6px' }} />Full Name</label>
                    <input
                      className="input-field"
                      required
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px' }} />Email</label>
                    <input
                      type="email"
                      className="input-field"
                      required
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email address"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>
                      <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px' }} />
                      Password {modalMode === 'edit' && <span style={{ color: '#888', fontWeight: 400 }}>(leave blank to keep current)</span>}
                    </label>
                    <input
                      type="password"
                      className="input-field"
                      required={modalMode === 'create'}
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                      placeholder={modalMode === 'create' ? 'Min. 6 characters' : 'New password (optional)'}
                      minLength={modalMode === 'create' ? 6 : undefined}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Status</label>
                    <select
                      className="input-field"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '18px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a' }}>Module Access</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {isEditingAdmin
                          ? 'Admins always have access to every module.'
                          : `Turn on the modules this staff user can see. (${enabledCount} enabled)`}
                      </div>
                    </div>
                  </div>
                  {!isEditingAdmin && modalMode === 'edit' && (
                    <div style={{ background: '#FFF8F0', border: '1px solid #FFE0B2', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#E65100' }}>
                      After changing modules, the staff user must log out and log back in for access to update.
                    </div>
                  )}

                  {isEditingAdmin ? (
                    <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '10px', padding: '14px', color: '#2E7D32', fontSize: '13px' }}>
                      Full access — all modules are available to admin accounts.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {APP_MODULES.map(mod => {
                        const on = mod.key === 'dashboard' ? true : !!formData.modules[mod.key];
                        const locked = mod.key === 'dashboard';
                        return (
                          <button
                            key={mod.key}
                            type="button"
                            onClick={() => toggleModule(mod.key)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              padding: '12px 14px',
                              borderRadius: '10px',
                              border: `1.5px solid ${on ? '#F47B20' : '#e5e7eb'}`,
                              background: on ? '#FFF8F0' : '#fafafa',
                              cursor: locked ? 'default' : 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.15s',
                              opacity: locked ? 0.95 : 1
                            }}
                          >
                            <span style={{ fontWeight: on ? 700 : 500, color: on ? '#E65100' : '#444', fontSize: '13px' }}>
                              {mod.label}{locked ? ' (always on)' : ''}
                            </span>
                            <FontAwesomeIcon
                              icon={on ? faToggleOn : faToggleOff}
                              style={{ fontSize: '26px', color: on ? '#F47B20' : '#c4c4c4' }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (modalMode === 'create' ? 'Create Staff User' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2><FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '10px', color: '#c62828' }} />Delete Staff User</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: '#444' }}>
                Delete <strong>{deleteTarget.name}</strong> ({deleteTarget.email})? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn-primary" style={{ background: '#c62828' }} onClick={handleDelete} disabled={formLoading}>
                {formLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
